/**
 * Common Zod validation schemas
 */

import { z } from 'zod';
import { AUTH_CONFIG } from '../auth/constants';

/**
 * Age band values - single source of truth
 * Used by: AgeBandSchema, AgeBand type, filter components, age-band selectors
 */
export const AGE_BAND_VALUES = ['3-4', '5-6', '7-8', '9-10'] as const;

/**
 * Age band validation schema
 */
export const AgeBandSchema = z.enum(AGE_BAND_VALUES);

/**
 * Generation mode values - single source of truth
 * Used by: GenerationModeSchema, GenerationMode type
 * 'gpt-translate' = GPT-5 English generation + 2-pass translation (legacy default)
 * 'claude-direct-fi' = Claude Sonnet 4.6 direct Finnish generation + editor pass
 * 'gpt-direct-fi' = GPT-5.4 direct Finnish generation + editor pass
 */
export const GENERATION_MODE_VALUES = [
  'gpt-translate',
  'claude-direct-fi',
  'gpt-direct-fi',
] as const;

/**
 * Generation mode validation schema
 */
export const GenerationModeSchema = z.enum(GENERATION_MODE_VALUES);

/**
 * Language validation schema
 */
export const LanguageSchema = z.enum(['fi', 'en', 'sv', 'no', 'da', 'de']);

/**
 * Email validation schema
 */
export const EmailSchema = z.string().email('Invalid email address');

/**
 * Password validation schema (FR111, updated Story 15.7, Story 23.12)
 * - Minimum 8 characters (Story 23.12: floor lowered 12 → 8, tied to
 *   AUTH_CONFIG.PASSWORD_MIN_LENGTH so this stays in lockstep with the signup
 *   rule and does not re-open client↔server drift)
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - Special character: OPTIONAL (encouraged via strength meter)
 *
 * Note: Special chars removed as mandatory to support Apple Keychain
 * and Google Password Manager auto-generated passwords.
 *
 * Consumed by ChangeEmailRequestSchema (account-schemas) to verify a user's
 * CURRENT password in the `changeEmail` Cloud Function. Keeping the min at 12
 * would have rejected a legitimately-created 8–11-char password on email change
 * once Story 23.12 lowered the signup floor. (No `.max` here on purpose: this is
 * a verification field, and creation is already capped at 64.)
 */
export const PasswordSchema = z
  .string()
  .min(AUTH_CONFIG.PASSWORD_MIN_LENGTH, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number');
// Story 15.7: Special character no longer required (Apple/Google compatibility)
